import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getAuthUser, logout } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  LogOut, Fuel, Loader2, Copy, ShoppingBag, User as UserIcon, TrendingUp, Droplets, RefreshCw, Camera, ImageIcon, X, CheckCircle2, Mail, Send,
} from "lucide-react";

export default function CustomerPortal() {
  const navigate = useNavigate();
  const user = getAuthUser();
  const [orders, setOrders] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState(null); // order object or null
  const [verifyForm, setVerifyForm] = useState({ name: "", last_name: "", amount: "" });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [verifyErrors, setVerifyErrors] = useState({});
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    const subj = contactSubject.trim();
    const msg = contactMessage.trim();
    if (subj.length < 2) return toast.error("Escribe un asunto");
    if (msg.length < 5) return toast.error("Escribe un mensaje más largo");
    setContactSending(true);
    try {
      await api.post("/customers/contact-admin", { subject: subj, message: msg });
      toast.success("Mensaje enviado al administrador");
      setContactSubject("");
      setContactMessage("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "No pudimos enviar tu mensaje. Intenta de nuevo.");
    } finally {
      setContactSending(false);
    }
  };

  const updateVerifyField = (key, value) => {
    setVerifyForm((f) => ({ ...f, [key]: value }));
    if (verifyErrors[key]) setVerifyErrors((p) => ({ ...p, [key]: undefined }));
  };

  const loadOrders = () => {
    api
      .get("/customers/me/orders")
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const openVerifyDialog = (order) => {
    // Pre-fill with the order's stored data so the customer only has to
    // adjust it to match what they actually typed on the Zelle app.
    const parts = (order.name || "").trim().split(/\s+/);
    const first = parts[0] || "";
    const rest = parts.slice(1).join(" ");
    setVerifyForm({
      name: first,
      last_name: rest,
      amount: (order.total ?? 0).toFixed(2),
    });
    setReceiptFile(null);
    setReceiptPreview(
      order.zelle_receipt_url
        ? `${process.env.REACT_APP_BACKEND_URL}${order.zelle_receipt_url}`
        : null,
    );
    setVerifyErrors({});
    setVerifyDialog(order);
  };

  const handleReceiptChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Selecciona una imagen (JPG, PNG, WebP, HEIC)");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Imagen demasiado grande (máx 8 MB)");
      return;
    }
    setReceiptFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(
      verifyDialog?.zelle_receipt_url
        ? `${process.env.REACT_APP_BACKEND_URL}${verifyDialog.zelle_receipt_url}`
        : null,
    );
    for (const id of ["receipt-camera-input", "receipt-gallery-input"]) {
      const el = document.getElementById(id);
      if (el) el.value = "";
    }
  };

  const uploadReceiptIfNeeded = async (orderId) => {
    if (!receiptFile) return true;
    setReceiptUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", receiptFile);
      await api.post(`/orders/${orderId}/zelle-receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el recibo");
      return false;
    } finally {
      setReceiptUploading(false);
    }
  };

  const submitVerify = async () => {
    if (!verifyDialog) return;
    const name = verifyForm.name.trim();
    const last_name = verifyForm.last_name.trim();
    const amount = parseFloat(verifyForm.amount);
    const errs = {};
    if (!name) errs.name = "Ingresa tu nombre";
    if (!last_name) errs.last_name = "Ingresa tu apellido";
    if (!verifyForm.amount || !isFinite(amount) || amount <= 0) errs.amount = "Monto inválido";
    setVerifyErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      toast.error(
        Object.keys(errs).length === 1
          ? errs[firstKey]
          : `Faltan ${Object.keys(errs).length} campos por completar`,
      );
      const map = { name: "verify-name-input", last_name: "verify-lastname-input", amount: "verify-amount-input" };
      const el = document.querySelector(`[data-testid="${map[firstKey]}"]`);
      if (el?.focus) setTimeout(() => el.focus(), 100);
      return;
    }
    setVerifyingId(verifyDialog.id);
    try {
      // Upload receipt (if provided) BEFORE calling verify — that way admin
      // has proof even if the auto-match fails.
      const uploaded = await uploadReceiptIfNeeded(verifyDialog.id);
      if (!uploaded) {
        setVerifyingId(null);
        return;
      }
      const { data } = await api.post(`/orders/${verifyDialog.id}/verify-zelle`, {
        name,
        last_name,
        amount,
      });
      const newStatus = data?.order?.status;
      const scan = data?.scan || {};
      if (newStatus && newStatus !== "pending") {
        toast.success(newStatus === "confirmed" ? "¡Pago confirmado! Tu código está activo." : `Estado: ${newStatus}`);
        setVerifyDialog(null);
      } else if (scan.matched > 0) {
        toast.success("¡Pago confirmado! Tu código está activo.");
        setVerifyDialog(null);
      } else if (scan.error === "amount_mismatch") {
        toast.error(
          `El monto que enviaste ($${scan.typed?.toFixed?.(2) ?? amount.toFixed(2)}) no coincide con el total del pedido ($${scan.expected?.toFixed?.(2)}). Contacta al administrador.`,
          { duration: 8000 },
        );
      } else if (scan.error === "saldo1980_not_configured") {
        toast.error("Verificación automática no disponible por ahora. Espera unos minutos.");
      } else {
        toast.info(
          "No encontramos tu depósito con esos datos. Revisa que el nombre y monto coincidan EXACTAMENTE con tu transferencia Zelle. Si acabas de enviarlo, espera 1-2 minutos.",
          { duration: 8000 },
        );
      }
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al verificar el pago");
    } finally {
      setVerifyingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const totalLiters = (orders || []).reduce((sum, o) => sum + (o.liters ?? o.gallons ?? 0), 0);
  const totalSpent = (orders || [])
    .filter((o) => o.status !== "cancelled")
    .reduce((sum, o) => sum + (o.total || 0), 0);
  const totalOrders = orders?.length ?? 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <BrandHeader
        right={
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-yellow-400/10 border border-yellow-400/20">
              <UserIcon className="w-3.5 h-3.5 text-yellow-400" />
              <span className="text-xs font-mono-display uppercase tracking-widest text-yellow-300">
                Cliente · {user?.name}
              </span>
            </div>
            <Button
              onClick={() => navigate("/")}
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-9"
              data-testid="new-order-header-btn"
            >
              Nuevo pedido
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Salir
            </Button>
          </div>
        }
      />

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-mono-display mb-2">
            Mi cuenta
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl">
            Hola, <span className="text-yellow-400">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-zinc-400 mt-2">Aquí tienes todo tu historial de pedidos.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard label="Pedidos totales" value={totalOrders} icon={ShoppingBag} testid="stat-orders" />
          <StatCard label="Litros comprados" value={totalLiters.toFixed(2)} icon={Droplets} testid="stat-liters" />
          <StatCard label="Total gastado" value={`$${totalSpent.toFixed(2)}`} icon={TrendingUp} highlight testid="stat-spent" />
        </div>

        <div className="border border-white/10 bg-zinc-900 rounded-xl overflow-x-auto">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <h2 className="font-display font-bold text-xl">Historial de pedidos</h2>
            <span className="text-xs uppercase tracking-widest font-mono-display text-zinc-500">
              {totalOrders} {totalOrders === 1 ? "orden" : "órdenes"}
            </span>
          </div>

          {orders === null ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
            </div>
          ) : orders.length === 0 ? (
            <div className="py-16 text-center">
              <Fuel className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 mb-6">Aún no tienes pedidos.</p>
              <Button
                onClick={() => navigate("/")}
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                data-testid="empty-new-order-btn"
              >
                Hacer mi primer pedido
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Fecha</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Combustible</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Litros</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Precio/L</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Total</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Código</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Estado</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} className="border-white/5" data-testid={`customer-order-row-${o.id}`}>
                    <TableCell className="text-sm text-zinc-300">
                      {new Date(o.created_at).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                      <div className="text-xs text-zinc-500">
                        {new Date(o.created_at).toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-yellow-200 font-medium">
                        {o.fuel_label || "Gasolina B94"}
                      </div>
                      {o.fuel_octane && (
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-display">
                          {o.fuel_octane}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-display">{o.liters ?? o.gallons}</TableCell>
                    <TableCell className="font-mono-display text-zinc-400">
                      ${(o.price_per_liter ?? o.price_per_gallon ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="font-mono-display font-bold text-yellow-400">
                      ${o.total.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {o.code ? (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(o.code);
                            toast.success("Código copiado");
                          }}
                          className="inline-flex items-center gap-1.5 font-mono-display font-bold text-yellow-300 tracking-widest hover:text-yellow-200 transition-colors"
                          data-testid={`copy-code-${o.id}`}
                        >
                          {o.code} <Copy className="w-3 h-3" />
                        </button>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={o.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      {o.status === "pending" && o.payment_method === "zelle" ? (
                        <Button
                          size="sm"
                          onClick={() => openVerifyDialog(o)}
                          disabled={verifyingId === o.id}
                          data-testid={`verify-zelle-${o.id}`}
                          className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-8"
                          title="Introduce los datos exactos que usaste en tu transferencia Zelle"
                        >
                          {verifyingId === o.id ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Verificando
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Verificar pago
                            </>
                          )}
                        </Button>
                      ) : (
                        <span className="text-zinc-700 text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Contact admin */}
        <div
          className="mt-8 border border-white/10 bg-zinc-900 rounded-xl overflow-hidden"
          data-testid="contact-admin-section"
        >
          <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
              <Mail className="w-4 h-4 text-yellow-400" />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl leading-tight">Contactar al administrador</h2>
              <p className="text-xs text-zinc-500 mt-0.5">
                Envía un mensaje directo. La respuesta te llegará a <span className="text-zinc-300">{user?.email}</span>.
              </p>
            </div>
          </div>
          <form onSubmit={submitContact} className="p-6 space-y-4">
            <div>
              <Label
                htmlFor="contact-subject"
                className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display"
              >
                Asunto
              </Label>
              <Input
                id="contact-subject"
                value={contactSubject}
                onChange={(e) => setContactSubject(e.target.value)}
                placeholder="Ej: Consulta sobre mi último pedido"
                maxLength={120}
                data-testid="contact-subject-input"
                className="mt-1.5 bg-zinc-950 border-white/10 text-white focus-visible:ring-yellow-400"
              />
            </div>
            <div>
              <Label
                htmlFor="contact-message"
                className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display"
              >
                Mensaje
              </Label>
              <Textarea
                id="contact-message"
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Escribe aquí tu mensaje. Sé claro para que podamos ayudarte más rápido."
                maxLength={2000}
                rows={6}
                data-testid="contact-message-input"
                className="mt-1.5 bg-zinc-950 border-white/10 text-white focus-visible:ring-yellow-400 resize-y"
              />
              <div className="text-[11px] text-zinc-600 font-mono-display mt-1 text-right">
                {contactMessage.length}/2000
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                Máx. 1 mensaje cada 2 min. Este correo se envía al administrador anclado en la configuración del portal.
              </div>
              <Button
                type="submit"
                disabled={contactSending || contactSubject.trim().length < 2 || contactMessage.trim().length < 5}
                data-testid="contact-submit-btn"
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {contactSending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1.5" />
                    Enviar mensaje
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Dialog open={!!verifyDialog} onOpenChange={(v) => !v && setVerifyDialog(null)}>
        <DialogContent
          data-testid="verify-zelle-dialog"
          className="bg-zinc-900 border-white/10 text-white max-w-md max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Verificar pago Zelle</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Introduce los datos <span className="text-yellow-300 font-semibold">exactamente</span> como aparecen
              en tu app de Zelle. Esto nos permite emparejar tu depósito con precisión.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className={`text-xs uppercase tracking-widest font-mono-display flex items-center gap-1.5 ${verifyErrors.name ? "text-red-400" : "text-zinc-400"}`}>
                Nombre (como en tu Zelle)
                {verifyErrors.name && <span className="text-red-500">*</span>}
              </Label>
              <Input
                data-testid="verify-name-input"
                value={verifyForm.name}
                onChange={(e) => updateVerifyField("name", e.target.value)}
                placeholder="Ej: Arturo"
                aria-invalid={!!verifyErrors.name}
                className={`mt-1 bg-zinc-800 transition-colors ${
                  verifyErrors.name
                    ? "border-red-500/70 focus-visible:ring-red-400/60 animate-shake"
                    : "border-white/10 focus-visible:ring-yellow-400"
                }`}
              />
              {verifyErrors.name && (
                <p className="text-[11px] text-red-400 font-medium mt-1 flex items-center gap-1" data-testid="verify-error-name">
                  <span className="inline-block w-1 h-1 rounded-full bg-red-500" />
                  {verifyErrors.name}
                </p>
              )}
            </div>
            <div>
              <Label className={`text-xs uppercase tracking-widest font-mono-display flex items-center gap-1.5 ${verifyErrors.last_name ? "text-red-400" : "text-zinc-400"}`}>
                Apellido (como en tu Zelle)
                {verifyErrors.last_name && <span className="text-red-500">*</span>}
              </Label>
              <Input
                data-testid="verify-lastname-input"
                value={verifyForm.last_name}
                onChange={(e) => updateVerifyField("last_name", e.target.value)}
                placeholder="Ej: Salazar"
                aria-invalid={!!verifyErrors.last_name}
                className={`mt-1 bg-zinc-800 transition-colors ${
                  verifyErrors.last_name
                    ? "border-red-500/70 focus-visible:ring-red-400/60 animate-shake"
                    : "border-white/10 focus-visible:ring-yellow-400"
                }`}
              />
              {verifyErrors.last_name && (
                <p className="text-[11px] text-red-400 font-medium mt-1 flex items-center gap-1" data-testid="verify-error-last_name">
                  <span className="inline-block w-1 h-1 rounded-full bg-red-500" />
                  {verifyErrors.last_name}
                </p>
              )}
            </div>
            <div>
              <Label className={`text-xs uppercase tracking-widest font-mono-display flex items-center gap-1.5 ${verifyErrors.amount ? "text-red-400" : "text-zinc-400"}`}>
                Monto enviado (USD)
                {verifyErrors.amount && <span className="text-red-500">*</span>}
              </Label>
              <Input
                data-testid="verify-amount-input"
                type="number"
                step="0.01"
                min="0"
                value={verifyForm.amount}
                onChange={(e) => updateVerifyField("amount", e.target.value)}
                placeholder="0.00"
                aria-invalid={!!verifyErrors.amount}
                className={`mt-1 bg-zinc-800 transition-colors ${
                  verifyErrors.amount
                    ? "border-red-500/70 focus-visible:ring-red-400/60 animate-shake"
                    : "border-white/10 focus-visible:ring-yellow-400"
                }`}
              />
              {verifyErrors.amount && (
                <p className="text-[11px] text-red-400 font-medium mt-1 flex items-center gap-1" data-testid="verify-error-amount">
                  <span className="inline-block w-1 h-1 rounded-full bg-red-500" />
                  {verifyErrors.amount}
                </p>
              )}
              {!verifyErrors.amount && verifyDialog && Math.abs(parseFloat(verifyForm.amount || 0) - verifyDialog.total) > 0.01 && (
                <p className="text-xs text-amber-400 mt-1.5">
                  ⚠ El total del pedido es ${verifyDialog.total.toFixed(2)}. Si enviaste otro monto, contacta al administrador.
                </p>
              )}
            </div>

            {/* Zelle receipt screenshot upload */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
                Foto del recibo Zelle (opcional pero recomendado)
              </Label>
              {receiptPreview ? (
                <div
                  className="mt-2 relative border border-white/10 rounded-lg overflow-hidden bg-zinc-950"
                  data-testid="verify-receipt-preview-wrapper"
                >
                  <img
                    src={receiptPreview}
                    alt="Recibo Zelle"
                    className="w-full max-h-64 object-contain"
                    data-testid="verify-receipt-preview"
                  />
                  {receiptFile && (
                    <div className="absolute bottom-2 left-2 bg-emerald-500/90 text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Listo para enviar
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={clearReceipt}
                    data-testid="verify-receipt-clear"
                    className="absolute top-2 right-2 bg-black/70 hover:bg-red-500/80 text-white rounded-full p-1.5 transition-colors"
                    aria-label="Quitar imagen"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label
                    htmlFor="receipt-camera-input"
                    data-testid="verify-receipt-camera"
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 hover:border-yellow-400/60 hover:bg-yellow-400/5 rounded-lg py-5 px-3 cursor-pointer transition-colors text-center"
                  >
                    <Camera className="w-6 h-6 text-yellow-400" />
                    <div>
                      <div className="text-xs font-bold text-white">Tomar foto</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">Cámara</div>
                    </div>
                  </label>
                  <label
                    htmlFor="receipt-gallery-input"
                    data-testid="verify-receipt-gallery"
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 hover:border-yellow-400/60 hover:bg-yellow-400/5 rounded-lg py-5 px-3 cursor-pointer transition-colors text-center"
                  >
                    <ImageIcon className="w-6 h-6 text-yellow-400" />
                    <div>
                      <div className="text-xs font-bold text-white">Subir de galería</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">Elegir archivo</div>
                    </div>
                  </label>
                </div>
              )}
              <input
                id="receipt-camera-input"
                data-testid="verify-receipt-input-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleReceiptChange}
                className="hidden"
              />
              <input
                id="receipt-gallery-input"
                data-testid="verify-receipt-input-gallery"
                type="file"
                accept="image/*"
                onChange={handleReceiptChange}
                className="hidden"
              />
              <p className="text-[10px] text-zinc-500 mt-2 text-center">
                JPG, PNG, WebP o HEIC · máx 8 MB
              </p>
              {receiptUploading && (
                <p className="text-xs text-yellow-300 mt-1.5 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Subiendo recibo…
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setVerifyDialog(null)}
              data-testid="verify-cancel-btn"
              className="text-zinc-400 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={submitVerify}
              disabled={verifyingId === verifyDialog?.id}
              data-testid="verify-submit-btn"
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
            >
              {verifyingId === verifyDialog?.id ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Verificando…
                </>
              ) : (
                <>Verificar ahora</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, highlight = false, testid }) {
  return (
    <div className="border border-white/10 bg-zinc-900 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono-display">
          {label}
        </span>
        <Icon className={`w-4 h-4 ${highlight ? "text-yellow-400" : "text-zinc-500"}`} />
      </div>
      <div className={`font-display font-black text-3xl ${highlight ? "text-yellow-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    dispatched: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    cancelled: "bg-zinc-700 text-zinc-400 border-zinc-600",
  };
  const labels = {
    pending: "Pendiente",
    confirmed: "Confirmada",
    dispatched: "Despachada",
    cancelled: "Cancelada",
  };
  return <Badge className={styles[status] || ""}>{labels[status] || status}</Badge>;
}
